import { FC, useRef, useState } from 'react'
import { FaDownload, FaFile, FaFileExcel, FaTrash } from 'react-icons/fa'
import CN from 'classnames'
import useUploadStatusUpdateFile from 'framework/api/methods/manageOrders/useUploadStatusUpdateFile'

import { PopUpAlert } from 'components/atoms'
import { Button } from 'components/atoms/Button'
import { Drawer } from 'components/atoms/Drawer'

import './uploadOrderFileStyles.css'

export interface UploadOrderStatusUpdateFileProps {
  className?: string | undefined
  [x: string]: any
}
export const UploadOrderStatusUpdateFile: FC<
  UploadOrderStatusUpdateFileProps
> = ({
  className,
  isActive,
  onCloseClick,
  notify,
  getBulkStatusUpdateLogList,
}: UploadOrderStatusUpdateFileProps) => {
  const InvoiceClasses = CN(
    'AddInvoice flex flex-col py-4 px-5 relative h-full overflow-y-auto overscroll-y-auto styled-scroll gap-y-5',
    className,
    {},
  )

  const [tableVariables, setTableVariables] = useState({
    take: 10,
    skip: 0,
    search: '',
  })
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [previews, setPreviews] = useState<string[]>([])
  const [deleteFilePopOutIsOpen, setDeleteFilePopOutIsOpen] = useState(false)
  const [deleteFile, SetDelFile] = useState<any>()
  const fileInputRef = useRef<HTMLInputElement>(null as any)

  // Function to select the right icon based on the file's MIME type
  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('xlsx')) {
      return <FaFileExcel style={{ color: 'green' }} />
    } else if (mimeType.includes('spreadsheetml')) {
      return <FaFileExcel style={{ color: 'green' }} />
    } else {
      return <FaFile style={{ color: 'red' }} />
    }
  }

  const handleFiles = (files: FileList | null) => {
    if (files) {
      const newFilesArray = Array.from(files)
      const validFiles = newFilesArray.filter((file) =>
        file.name.endsWith('.xlsx'),
      )

      if (validFiles.length === 0) {
        notify('Only Excel(.xlsx) files are allowed', 'error')
        return
      }
      setSelectedFiles((prevFiles) => [...prevFiles, ...newFilesArray])
      const newPreviews = newFilesArray.map(
        (file) =>
          file.type.startsWith('image/')
            ? URL.createObjectURL(file)
            : 'placeholder', // Use 'placeholder' for non-image files
      )
      setPreviews((prevPreviews) => [...prevPreviews, ...newPreviews])
      // Reset the file input after the state is updated
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDownloadFile = (file: any) => {
    // Create a URL for the file
    const url = window.URL.createObjectURL(file)
    const a = document.createElement('a')
    a.href = url
    a.download = file.name || 'download'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }
  const handleDeleteFile = (index: number) => {
    setDeleteFilePopOutIsOpen(true)
    handleRemoveFile(index)
  }
  const handleRemoveFile = (index: number) => {
    setDeleteFilePopOutIsOpen(false)
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))
    setPreviews((prevPreviews) => {
      // Revoke the object URL to avoid memory leaks
      URL.revokeObjectURL(prevPreviews[index])
      return prevPreviews.filter((_, i) => i !== index)
    })
    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  /** API Call for create cutoff time using react-query */
  const {
    mutate: uploadBulkStatusUpdateFileMutate,
    isLoading: uploadFileIsLoading,
  } = useUploadStatusUpdateFile()
  /** Process the create cutoff time */
  async function uploadFile() {
    uploadBulkStatusUpdateFileMutate(
      {
        originalFileName: selectedFiles[0].name,
        orderFile: selectedFiles,
      },
      {
        onSuccess: ({ data: successData }: any) => {
          notify(successData.message, 'success')
          onCloseClick()
          getBulkStatusUpdateLogList(tableVariables.take, tableVariables.skip)
        },
        onError: ({ response: { data: errData } }: any) => {
          // const match = errData.match(
          //   /System\.Exception:\s*(.*?)\s*(?:\n\s*at\s)/,
          // )
          // const trimmedError = match?.[1]?.trim() || 'Something went wrong'
          onCloseClick()
          getBulkStatusUpdateLogList(tableVariables.take, tableVariables.skip)
          notify(errData.message || 'Error', 'error')
        },
      },
    )
  }

  const handleUploadClick = () => {
    uploadFile()
  }
  return (
    <Drawer
      isActive={isActive}
      onCloseClick={onCloseClick}
      width={700}
      margin-left={10}
      title='Upload Order Status Update File'>
      <div className={InvoiceClasses}>
        <div
          className='add-file-container'
          style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div className='add-file-upload w-full'>
            <div className='Addfile flex flex-col py-4 px-5 relative gap-y-5'>
              <label htmlFor='file-upload' className='block text-N-700 mb-0'>
                Upload File
                <span style={{ color: 'orange' }}> *(Excel file only)</span>
              </label>
              <div
                className='border-2 border-dashed border-N-200 rounded p-8 text-center relative'
                hidden={selectedFiles.length > 0}>
                <input
                  id='file-upload'
                  type='file'
                  multiple
                  onChange={(e) => handleFiles(e.target.files)}
                  className='absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer'
                  ref={fileInputRef}
                />
                <div className='text-N-600'>
                  <span style={{ color: 'orange' }}>Choose file</span> or drop
                  here
                </div>
              </div>
              <ul className='mt-2'>
                {selectedFiles.map((file, index) => (
                  <li
                    key={index}
                    className='flex items-center border p-2 rounded mb-2 justify-between'>
                    <div className='flex items-center'>
                      {previews[index] !== 'placeholder' ? (
                        <img
                          src={previews[index]}
                          alt={`Preview ${file.name}`}
                          className='w-10 h-10 mr-2'
                        />
                      ) : (
                        <i className='file-icon mr-2'>
                          {getFileIcon(file.type)}
                        </i> // Placeholder icon for non-image files
                      )}
                      <span
                        style={{
                          color: 'gray',
                          fontWeight: 'bold',
                          fontSize: '13px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '150px', // Adjust the maxWidth to fit your layout
                        }}
                        title={file.name} // Shows the full name on hover
                      >
                        {file.name}
                      </span>
                    </div>
                    <div className='flex items-center'>
                      <FaDownload
                        style={{
                          color: 'green',
                          fontSize: '1.00rem', // Size can be adjusted if needed
                          cursor: 'pointer',
                          marginLeft: '0.5rem',
                        }}
                        onClick={() => handleDownloadFile(file)}
                      />
                      <FaTrash
                        style={{
                          color: 'maroon',
                          fontSize: '1.00rem', // Size can be adjusted if needed
                          cursor: 'pointer',
                          marginLeft: '0.5rem',
                        }}
                        onClick={() => handleRemoveFile(index)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className='drawer-footer flex items-center w-full h-[67px] sticky bottom-0 px-5 border-t border-N-200 shadow-sm gap-x-5'>
        <Button
          isLoading={uploadFileIsLoading}
          disabled={uploadFileIsLoading || selectedFiles.length === 0}
          isFullWidth
          onClick={() => {
            const invalidFile = selectedFiles.find(
              (file) => !file.name.endsWith('.xlsx'),
            )
            if (invalidFile) {
              notify('Only .xlsx files can be submitted', 'error')
              return
            }
            handleUploadClick()
          }}>
          Submit
        </Button>
        <Button isFullWidth appearance='dull' onClick={onCloseClick}>
          Close
        </Button>
      </div>
      {deleteFilePopOutIsOpen && (
        <PopUpAlert
          isActive={deleteFilePopOutIsOpen}
          title='Delete Document?'
          message='Are you sure you want to proceed? This is not reversible.'
          cancelButtonText='Cancel'
          confirmButtonText='Yes, I’m Sure'
          onCancelClick={() => setDeleteFilePopOutIsOpen(false)}
          onOverlayClick={() => setDeleteFilePopOutIsOpen(false)}
          onConfirmClick={() => {
            handleDeleteFile(deleteFile)
          }}
        />
      )}
      ;
    </Drawer>
  )
}
UploadOrderStatusUpdateFile.defaultProps = {
  className: undefined,
}
export default UploadOrderStatusUpdateFile
